// BUILD-042 cycle 13 - pure model for the checklist work area (assign panel, assigned-checklists card, document link).
// No React, no I/O. The date arithmetic mirrors the SERVER exactly (hr-data.ts createChecklistAssignment): due_date = anchor_date + due_offset_days (calendar days, negative = before).
// Nothing here is an authorization decision or a stored-semantics change; the server stays the authority for every rule.

export type ChecklistKind = 'onboarding' | 'offboarding';

export interface PreviewTemplateTask { taskText: string; responsibleRole: string; dueOffsetDays: number; sortOrder: number }
export interface PreviewAssignmentTask { status: 'not_started' | 'in_progress' | 'completed' | 'exception' }
export interface PreviewAssignment { assignmentId: string; templateId: string; assignedAt: string; tasks: PreviewAssignmentTask[] }

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** A real calendar date in YYYY-MM-DD form (the date input yields '' for an invalid or partial entry). */
export function isRealDate(s: unknown): s is string {
  if (typeof s !== 'string' || !DATE_ONLY.test(s)) return false;
  const t = Date.parse(`${s}T00:00:00Z`);
  return !Number.isNaN(t) && new Date(t).toISOString().slice(0, 10) === s;
}

/** anchor + offset calendar days, as YYYY-MM-DD (UTC arithmetic: no time-zone or DST shift). null when the anchor is not a real date. */
export function dueDateFor(anchorDate: string, dueOffsetDays: number): string | null {
  if (!isRealDate(anchorDate) || !Number.isFinite(dueOffsetDays)) return null;
  const t = Date.parse(`${anchorDate}T00:00:00Z`) + Math.trunc(dueOffsetDays) * 86400000;
  return new Date(t).toISOString().slice(0, 10);
}

/** What each task of the chosen template would be due, from the chosen date. Sorted like the assigned checklist (sortOrder). */
export function buildDuePreview(tasks: PreviewTemplateTask[], anchorDate: string): Array<{ taskText: string; responsibleRole: string; dueDate: string | null }> {
  return [...tasks].sort((a, b) => a.sortOrder - b.sortOrder).map((t) => ({ taskText: t.taskText, responsibleRole: t.responsibleRole, dueDate: dueDateFor(anchorDate, t.dueOffsetDays) }));
}

/** The reference the template editor uses for each process kind. */
export function referenceDateName(kind: ChecklistKind | undefined): string {
  return kind === 'offboarding' ? 'separation date' : kind === 'onboarding' ? 'hire date' : 'chosen date';
}

export function dateFieldLabel(kind: ChecklistKind | undefined): string {
  return kind === 'offboarding' ? 'Count due dates from (separation date)' : kind === 'onboarding' ? 'Count due dates from (hire date)' : 'Count due dates from';
}

export function dateFieldExplanation(kind: ChecklistKind | undefined): string {
  const ref = kind === 'offboarding' ? 'the separation date' : kind === 'onboarding' ? 'the hire date' : 'this date';
  return `Each task is due this many days before or after this date, as set in the template. It starts as ${ref} and only affects this assignment.`;
}

export function progressLabel(done: number, total: number): string {
  return total === 0 ? 'No tasks' : `${done} of ${total} ${total === 1 ? 'task' : 'tasks'} done`;
}

export function doneCount(tasks: PreviewAssignmentTask[]): number {
  return tasks.filter((t) => t.status === 'completed').length;
}

/** The server's definition of "still open" for the duplicate refusal: zero tasks, or a task not started / in progress. A finished one (all completed/excepted) is history. */
export function isLiveAssignment(a: { tasks: PreviewAssignmentTask[] }): boolean {
  return a.tasks.length === 0 || a.tasks.some((t) => t.status === 'not_started' || t.status === 'in_progress');
}

export function findLiveForTemplate<A extends PreviewAssignment>(assignments: A[], templateId: string): A | undefined {
  return assignments.find((a) => a.templateId === templateId && isLiveAssignment(a));
}

/** Most recent FINISHED assignment of this template (a deliberate re-assignment after it is allowed and creates a separate one). */
export function findFinishedForTemplate<A extends PreviewAssignment>(assignments: A[], templateId: string): A | undefined {
  return [...assignments].filter((a) => a.templateId === templateId && !isLiveAssignment(a))
    .sort((a, b) => new Date(b.assignedAt).getTime() - new Date(a.assignedAt).getTime())[0];
}

export function sortNewestFirst<A extends { assignedAt: string }>(assignments: A[]): A[] {
  return [...assignments].sort((a, b) => new Date(b.assignedAt).getTime() - new Date(a.assignedAt).getTime());
}

/** The employee's Documents section, opened on one document assignment (the existing section / item parameters of the employee page). */
export function documentSectionPath(employeeId: string, documentAssignmentId: string): string {
  return `/hr/admin/employees/${encodeURIComponent(employeeId)}?section=documents&item=${encodeURIComponent(documentAssignmentId)}`;
}

/** An attention/"View tasks" focus id may name an assignment or a task: does this assignment hold it? */
export function holdsFocus(a: { assignmentId: string; tasks: Array<{ assignmentTaskId: string }> }, focusId: string | null | undefined): boolean {
  return !!focusId && (a.assignmentId === focusId || a.tasks.some((t) => t.assignmentTaskId === focusId));
}

export const EMPTY_TITLE: Record<ChecklistKind, string> = {
  onboarding: 'No onboarding checklist assigned.',
  offboarding: 'No offboarding checklist assigned.',
};
export const EMPTY_HINT = 'Use Assign checklist to choose a template.';
export const NOTICE_MS = 5000;
